import { useRef, useState } from "react";
import { IMAGE_SHAPES, type CropRegion } from "../api";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";
import {
  initialBox,
  moveBox,
  parseAspect,
  resizeBox,
  toRegion,
  type Box,
  type Corner,
} from "./crop";

/**
 * Cropper is a frame over a picture that the author drags into place
 * (ADR-0048). What it produces is a region in fractions; the server does the
 * cutting, because the browser cannot read a stored picture's pixels.
 *
 * Pointer events and nothing else, so a thumb, a mouse and a pen are one code
 * path. The arithmetic is in `crop.ts`; this file only turns pointer movement
 * into picture pixels and back.
 */
export default function Cropper({
  src,
  shape,
  lockedNote,
  applying,
  error,
  onApply,
  onCancel,
}: {
  src: string;
  /**
   * The shape the frame must keep, "w:h", or null for a picture whose shape
   * is the author's to choose — then the shape chips are offered.
   */
  shape: string | null;
  /** Why the shape is fixed, said once under the picture. */
  lockedNote?: string;
  applying: boolean;
  /** The crop's failure, said under the picture. */
  error: unknown;
  onApply: (region: CropRegion) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const img = useRef<HTMLImageElement>(null);
  // The picture's own size, upright — the browser applies a photo's EXIF turn
  // to what it reports, the same turn the server applies (ADR-0048).
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [chosen, setChosen] = useState<string | null>(shape);
  const aspect = parseAspect(shape ?? chosen);
  const [box, setBox] = useState<Box | null>(null);
  const drag = useRef<{
    id: number;
    corner: Corner | null;
    startX: number;
    startY: number;
    start: Box;
  } | null>(null);

  // Screen pixels to picture pixels: the picture is shown scaled.
  const scale = () => {
    const shown = img.current?.getBoundingClientRect().width ?? 0;
    return size && shown > 0 ? size.w / shown : 1;
  };

  const begin = (corner: Corner | null) => (e: React.PointerEvent) => {
    if (!box || applying) return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    drag.current = { id: e.pointerId, corner, startX: e.clientX, startY: e.clientY, start: box };
  };
  const follow = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId || !size) return;
    const k = scale();
    const dx = (e.clientX - d.startX) * k;
    const dy = (e.clientY - d.startY) * k;
    setBox(
      d.corner
        ? resizeBox(d.start, d.corner, dx, dy, size.w, size.h, aspect)
        : moveBox(d.start, dx, dy, size.w, size.h),
    );
  };
  const end = (e: React.PointerEvent) => {
    if (drag.current?.id === e.pointerId) drag.current = null;
  };

  const pct = (v: number, of: number) => `${(v / of) * 100}%`;

  return (
    <div data-testid="cropper">
      {shape === null && (
        <fieldset className="mb-2 flex flex-wrap items-center gap-1.5">
          <legend className="sr-only">{t.images.shape}</legend>
          {[null, ...IMAGE_SHAPES].map((option) => (
            <button
              key={option ?? "free"}
              type="button"
              disabled={applying}
              aria-pressed={chosen === option}
              data-testid={`crop-shape-${option ? option.replace(":", "-") : "free"}`}
              onClick={() => {
                setChosen(option);
                if (size) setBox(initialBox(size.w, size.h, parseAspect(option)));
              }}
              className={`rounded-full border px-2.5 py-1 text-xs transition-colors disabled:opacity-40 ${
                chosen === option
                  ? "border-sage bg-sage-soft font-medium text-sage-deep"
                  : "border-line text-muted hover:text-ink"
              }`}
            >
              {option ? (t.images.shapeNames[option] ?? option) : t.crop.free}
            </button>
          ))}
        </fieldset>
      )}

      {/* Physical coordinates throughout: a picture has no reading direction,
          so the frame is placed by left and top in either interface language. */}
      <div className="flex justify-center rounded-card bg-sunk p-2">
        <div className="relative touch-none overflow-hidden select-none" dir="ltr">
          <img
            ref={img}
            src={src}
            alt=""
            draggable={false}
            data-testid="crop-image"
            onLoad={(e) => {
              const el = e.currentTarget;
              const natural = { w: el.naturalWidth, h: el.naturalHeight };
              setSize(natural);
              setBox(initialBox(natural.w, natural.h, aspect));
            }}
            className="block max-h-[55dvh] w-auto max-w-full"
          />
          {size && box && (
            <div
              data-testid="crop-frame"
              data-region={JSON.stringify(toRegion(box, size.w, size.h))}
              onPointerDown={begin(null)}
              onPointerMove={follow}
              onPointerUp={end}
              onPointerCancel={end}
              style={{
                left: pct(box.x, size.w),
                top: pct(box.y, size.h),
                width: pct(box.w, size.w),
                height: pct(box.h, size.h),
              }}
              /* The shadow darkens everything outside the frame; the parent
                 clips it to the picture. */
              className="absolute cursor-move border-2 border-white shadow-[0_0_0_9999px_rgb(0_0_0/0.55)]"
            >
              {(["nw", "ne", "sw", "se"] as const).map((corner) => (
                <span
                  key={corner}
                  data-testid={`crop-handle-${corner}`}
                  onPointerDown={begin(corner)}
                  onPointerMove={follow}
                  onPointerUp={end}
                  onPointerCancel={end}
                  /* 32px of touch target inside the corner, so a frame on the
                     picture's edge is not half clipped away. */
                  className={`absolute grid size-8 ${
                    corner === "nw"
                      ? "top-0 left-0 cursor-nwse-resize items-start justify-items-start"
                      : corner === "ne"
                        ? "top-0 right-0 cursor-nesw-resize items-start justify-items-end"
                        : corner === "sw"
                          ? "bottom-0 left-0 cursor-nesw-resize items-end justify-items-start"
                          : "right-0 bottom-0 cursor-nwse-resize items-end justify-items-end"
                  }`}
                >
                  <span className="size-4 rounded-sm border-2 border-white bg-sage shadow" />
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="mt-1.5 text-[0.7rem] text-muted">{t.crop.hint}</p>
      {lockedNote && <p className="mt-0.5 text-[0.7rem] text-muted">{lockedNote}</p>}
      <MutationError className="mt-2" testId="crop-error" error={error} fallback={t.crop.failed} />

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          data-testid="crop-cancel"
          disabled={applying}
          onClick={onCancel}
          className="flex-1 rounded-control border border-line px-4 py-2.5 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
        >
          {t.crop.cancel}
        </button>
        <button
          type="button"
          data-testid="crop-apply"
          disabled={applying || !size || !box}
          onClick={() => size && box && onApply(toRegion(box, size.w, size.h))}
          className="flex-1 rounded-control bg-sage px-4 py-2.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
        >
          {applying ? t.crop.applying : t.crop.apply}
        </button>
      </div>
    </div>
  );
}
